import { useState } from "react";
import { toast } from "sonner";
import { useSavedAddresses, SavedAddress } from "../hooks/useSavedAddresses";
import AddressForm from "./AddressForm";

export default function SavedAddresses({ onSaved }: { onSaved: () => void }) {
  const { addresses, query, save, remove, proof, proofEnabled } = useSavedAddresses();
  const [proofType, setProofType] = useState('utility_bill');
  const [editing, setEditing] = useState<SavedAddress | null | undefined>(undefined);
  const [makeCurrent, setMakeCurrent] = useState(false);
  const busy = save.isLoading || remove.isLoading || proof.isLoading;

  if (query.isLoading) return <p role="status">Loading your saved addresses...</p>;
  if (query.isError) return <div role="alert">
    <p>We could not load your addresses. Your existing details have not changed.</p>
    <button type="button" onClick={() => query.refetch()} className="mt-3 text-visaro-orange">Try again</button>
  </div>;

  return <div className="flex flex-col gap-6">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="font-bold text-lg text-deepBlue">Your saved addresses</h2>
        <p className="text-sm text-gray-500">Keep up to three addresses. Your current residence determines your KYC requirements.</p>
      </div>
      <span className="text-sm whitespace-nowrap text-gray-500">{addresses.length} / 3 saved</span>
    </div>
    {addresses.map(address => <div key={address.id} className={`rounded-xl border p-4 ${address.is_current ? "border-visaro-orange bg-orange-50/30" : "border-gray-200"}`}>
      <p className="text-xs font-semibold text-gray-500 mb-2">{address.is_current ? "Current residence" : address.legacy_text && !address.country ? "Previously saved address" : "Saved address"}</p>
      <p className="text-sm text-deepBlue whitespace-pre-line">{address.line_1 ? [address.line_1, address.line_2, address.city, address.state, address.postcode, address.country].filter(Boolean).join(", ") : address.legacy_text && !/^(undefined|null)$/i.test(address.legacy_text.trim()) ? address.legacy_text : "This older record has no usable address. Edit it to add the details, or remove it."}</p>
      <div className="flex gap-5 mt-3 text-sm">
        <button type="button" disabled={busy} className="text-visaro-orange font-semibold" onClick={() => {
          setEditing(address); setMakeCurrent(address.is_current);
        }}>Edit{address.is_current ? "" : " or set as current"}</button>
        {!address.is_current && <button type="button" disabled={busy} className="text-gray-500" onClick={async () => {
          if (!window.confirm("Remove this saved address and its uploaded evidence? Your current residence will not change.")) return;
          try { await remove.mutateAsync(address); if (editing?.id === address.id) setEditing(undefined); }
          catch (error: any) { toast.error(error?.message || "Could not remove the address"); }
        }}>Remove</button>}
      </div>
      {proofEnabled && <details className="mt-4 text-sm text-gray-600">
        <summary className="cursor-pointer">Proof of address, optional{address.proof_uploaded_at ? ' · Document uploaded' : ''}</summary>
        <p className="my-3">You can upload a recent utility bill or bank statement showing your name and this address. Uploading is optional and does not verify your address automatically.</p>
        {address.proof_uploaded_at && <p className="mb-3">{address.proof_address_version === address.version ? 'Evidence uploaded, not verified.' : 'The address has changed since the evidence was uploaded.'}</p>}
        <label className="block mb-2">Document type
          <select value={proofType} onChange={event => setProofType(event.target.value)} disabled={busy} className="block border rounded p-2 mt-1">
            <option value="utility_bill">Utility bill</option><option value="bank_statement">Bank statement</option>
          </select>
        </label>
        <label className="block">Upload PDF, JPG or PNG, up to 10 MB
          <input type="file" accept="application/pdf,image/jpeg,image/png" disabled={busy} className="block mt-2 max-w-full"
            onChange={async event => {
              const file = event.target.files?.[0]; event.target.value = '';
              if (!file) return;
              if (file.size > 10 * 1024 * 1024) { toast.error('Choose a file smaller than 10 MB'); return; }
              try { await proof.mutateAsync({ address, file, type: proofType }); toast.success('Evidence uploaded, not verified'); }
              catch (error: any) { toast.error(error?.message || 'Could not upload evidence. Your address is still saved.'); }
            }} />
        </label>
      </details>}
    </div>)}
    {editing === undefined ? <button type="button" disabled={addresses.length >= 3 || busy}
      onClick={() => { setEditing(null); setMakeCurrent(false); }}
      className="self-start rounded-xl border border-visaro-orange px-5 py-3 text-visaro-orange font-semibold disabled:opacity-50">
      Add another address
    </button> : <section className="border-t border-gray-200 pt-6 flex flex-col gap-5">
      <div className="flex justify-between gap-4">
        <h3 className="font-bold text-deepBlue">{editing ? "Edit address" : "Add an address"}</h3>
        <button type="button" disabled={busy} onClick={() => setEditing(undefined)} className="text-sm text-gray-500">Cancel</button>
      </div>
      <label className="flex items-start gap-3 rounded-lg bg-gray-50 p-4 text-sm">
        <input type="checkbox" checked={makeCurrent} disabled={editing?.is_current || busy} onChange={event => setMakeCurrent(event.target.checked)} className="mt-1" />
        <span>I currently live at this address. Saving it as my current residence will update my country of residence. Other saved addresses will be kept.</span>
      </label>
      <AddressForm key={editing?.id ?? "new"} initialAddress={editing} isSaving={save.isLoading} onSaved={() => {}}
        onSubmitAddress={async address => {
          try {
            await save.mutateAsync({ ...address, id: editing?.id, version: editing?.version, make_current: makeCurrent });
            toast.success("Address saved"); setEditing(undefined);
          } catch (error: any) { toast.error(error?.message || "Could not save your address"); }
        }} />
    </section>}
    {editing === undefined && <button type="button" disabled={busy} onClick={onSaved}
      className="self-start rounded-xl bg-visaro-orange px-6 py-3 font-semibold text-white disabled:opacity-50">
      Continue
    </button>}
  </div>;
}
